import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell } from 'lucide-react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../lib/useAuth'

// In-app notification bell. Counts unread non-dismissed
// player_notifications rows for the current user and links to the
// full feed at /notifications. Refreshes on mount, on a 30s poll
// for the badge, and on a postgres_changes INSERT subscription so a
// brand-new notif bumps the count immediately without a refresh.
export default function NotificationBell({ tone = 'recon' }) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [unread, setUnread] = useState(0)

  useEffect(() => {
    if (!user?.id) return
    let cancelled = false
    const load = async () => {
      const { count } = await supabase
        .from('player_notifications')
        .select('id', { count: 'exact', head: true })
        .eq('recipient_id', user.id)
        .eq('read', false)
        .eq('dismissed', false)
      if (!cancelled) setUnread(count || 0)
    }
    load()
    const poll = setInterval(load, 30000)
    const channel = supabase
      .channel(`notif-bell-${user.id}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'player_notifications', filter: `recipient_id=eq.${user.id}` },
        () => setUnread(c => c + 1))
      .subscribe()
    return () => {
      cancelled = true
      clearInterval(poll)
      try { supabase.removeChannel(channel) } catch { /* ignore */ }
    }
  }, [user?.id])

  const tones = tone === 'cipher'
    ? { fg: '#00ff66', badge: '#ff2a2a' }
    : { fg: '#9adb6e', badge: '#ff6a3d' }

  return (
    <button
      onClick={() => navigate('/notifications')}
      title="Notifications"
      style={{
        position: 'relative',
        background: 'none', border: 'none', padding: 8, cursor: 'pointer',
        color: tones.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minHeight: 44, minWidth: 44,
      }}>
      <Bell size={18} />
      {unread > 0 && (
        <span style={{
          position: 'absolute', top: 4, right: 4,
          background: tones.badge, color: '#000',
          fontSize: 10, fontWeight: 700, lineHeight: 1,
          padding: '2px 5px', minWidth: 14, textAlign: 'center',
          borderRadius: 8, fontFamily: "'JetBrains Mono', monospace",
        }}>
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </button>
  )
}
